import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { Debt } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { reportError } from "src/utils/error-reporting";
import { useMounted } from "../use-mounted";

// Action Types
type DebtStoreAction =
  | { type: "SET_DEBT"; payload: Debt[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_DEBT"; payload: Debt }
  | { type: "DELETE_DEBT"; payload: string }
  | { type: "SET_DEBT_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_DEBT_SAVING_NEW"; payload: boolean };

// Reducer Function
const debtStoreReducer = (
  state: DebtStoreState,
  action: DebtStoreAction
): DebtStoreState => {
  switch (action.type) {
    case "SET_DEBT":
      return {
        ...state,
        debt: action.payload,
        debtLoading: false,
      };

    case "SET_LOADING":
      return { ...state, debtLoading: action.payload };

    case "ADD_OR_UPDATE_DEBT":
      const index = state.debt.findIndex((l) => l.id === action.payload.id);
      let updatedDebt = [...state.debt];
      if (index > -1) {
        updatedDebt[index] = action.payload;
      } else {
        updatedDebt.push(action.payload);
      }
      return { ...state, debt: updatedDebt };

    case "DELETE_DEBT":
      return {
        ...state,
        debt: state.debt.filter((l) => l.id !== action.payload),
      };
    case "SET_DEBT_FILES_TO_UPLOAD":
      return { ...state, debtFilestoUpload: action.payload };
    case "SET_DEBT_SAVING_NEW":
      return { ...state, debtSavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface DebtStoreState {
  debt: Debt[];
  debtLoading: boolean;
  debtSavingNew: boolean;
  debtFilestoUpload: File[];
}

// Hook
export const useDebtStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(debtStoreReducer, {
    debt: [],
    debtLoading: true,
    debtFilestoUpload: [],
    debtSavingNew: false,
  });

  const deleteDebtById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchDebtData = useV3GenericGetData<Debt[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createDebt = useV3Create<Debt>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateDebt = useV3Update<Debt>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<Debt>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handleDebtFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.debtFilestoUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "debts",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_DEBT",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_DEBT_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId}`));
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId} with message "${error}"`));
        }
      }
    },
    [state.debtFilestoUpload, uploadDocument, ud_id]
  );

  const handleDownloadDebtDocument = useCallback(
    async (debt_id: string, document_id: string | null) => {
      if (!document_id || !debt_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      toast.success("Downloading file...");

      const params: any = {
        document_id: document_id,
        entity: "debts",
        entity_id: debt_id,
        ud_id: ud_id,
      };

      downloadDocument(params)
        .then((res: any) => {
          if (res) {
            // download File from s3 res and get file name from mathcing document id in debt objects' documents array
            downloadFileFromS3(
              res,
              state.debt
                ?.find((p) => p.id === debt_id)
                ?.documents?.find((d) => d.document_id === document_id)
                ?.document_name || "Snug_Download.error"
            );
          } else {
            reportError(new Error(`Document Error: Failed to get document with message "No response"`));
          }
        })
        .catch((err) => {
          toast.error("Failed to download file");
          reportError(new Error(`Document Error: Failed to get document with message "${err}"`));
        });
    },
    [downloadDocument, state.debt, ud_id]
  );

  const handleDeleteDebtDocument = useCallback(
    async (debt_id: string, document_id: string | null): Promise<void> => {
      if (!document_id || !debt_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      try {
        dispatch({ type: "SET_DEBT_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "debts",
          entity_id: debt_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the debt object
          const updatedDebt = state.debt.map((p) => {
            if (p.id === debt_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_DEBT",
            payload: updatedDebt,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_DEBT_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.debt, deleteDocumentById, ud_id]
  );

  const handleDebtFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_DEBT_FILES_TO_UPLOAD",
        payload: [...state.debtFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.debtFilestoUpload]
  );

  const handleDebtFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.debtFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_DEBT_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.debtFilestoUpload]
  );

  const handleDebtFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_DEBT_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchDebtItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchDebtData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "debts",
        });

        if (isMounted()) {
          dispatch({ type: "SET_DEBT", payload: response });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchDebtData, isMounted]
  );

  const handleAddOrUpdateDebtSingleCall = useCallback(
    async (debtItem: Debt): Promise<Debt | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(debtItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updateDebt(
            {
              ud_id: ud_id,
              entity: "debts",
              entity_id: debtItem.id || undefined,
            },
            debtItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createDebt(
            { ud_id: ud_id, entity: "debts" }, // Adjust according to your actual API parameter requirements
            debtItem
          );
        }
        dispatch({ type: "ADD_OR_UPDATE_DEBT", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save debt item");
        console.error(error);
        return null;
      }
    },
    [ud_id, createDebt, updateDebt]
  );

  const handleAddOrUpdateDebt = useCallback(
    async (debtItem: Debt) => {
      dispatch({ type: "SET_DEBT_SAVING_NEW", payload: true });
      if (state.debtFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateDebtSingleCall(debtItem);
          if (res) {
            await handleDebtFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error("Failed to add or edit debt data. Please try again.");
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_DEBT_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdateDebtSingleCall(debtItem);
        } catch (error) {
          toast.error("Failed to add or edit debt data. Please try again.");
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_DEBT_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdateDebtSingleCall,
      state.debtFilestoUpload,
      handleDebtFilesUpload,
    ]
  );

  const handleDeleteDebt = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_DEBT_SAVING_NEW", payload: true });
      try {
        if (!ud_id) {
          return Promise.reject("No ud_id provided");
        }
        if (!id) {
          return Promise.reject("No id provided");
        }
        await deleteDebtById({
          ud_id: ud_id,
          entity: "debts",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_DEBT", payload: id });
        
      } catch (error) {
        toast.error("Failed to delete item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_DEBT_SAVING_NEW", payload: false });
      }
    },
    [deleteDebtById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchDebtItems(ud_id); // Fetch all debt items
    }
  }, [ fetchDebtItems, ud_id]);

  return {
    debt: state.debt,
    debtLoading: state.debtLoading,
    debtSavingNew: state.debtSavingNew,
    debtFilestoUpload: state.debtFilestoUpload,
    handleAddOrUpdateDebt,
    handleDeleteDebt,
    handleDebtFileRemove,
    handleDebtFilesDrop,
    handleDebtFilesRemoveAll,
    handleDownloadDebtDocument,
    handleDeleteDebtDocument,
    handleDebtFilesUpload,
  };
};
